import type { RateOrInherit } from '@finsim/model';
import { useTranslation } from 'react-i18next';
import { PercentField, Select } from '../../../shared/ui';

export type RateKind = RateOrInherit['kind'];

function convert(spec: RateOrInherit, kind: RateKind): RateOrInherit {
  const centre =
    spec.kind === 'fixed'
      ? spec.rate
      : spec.kind === 'distribution'
        ? spec.mean
        : spec.kind === 'cpiRelative'
          ? spec.offset
          : 0.02;
  switch (kind) {
    case 'fixed':
      return { kind: 'fixed', rate: centre };
    case 'cpiRelative':
      return { kind: 'cpiRelative', offset: spec.kind === 'cpiRelative' ? spec.offset : 0 };
    case 'distribution':
      return { kind: 'distribution', mean: centre, volatility: 0.1 };
    case 'inherit':
      return { kind: 'inherit' };
  }
}

export interface RateValueEditorProps {
  value: RateOrInherit;
  onChange: (value: RateOrInherit) => void;
  kinds: readonly RateKind[];
  /** Accessible name prefix (e.g. the field or period label). */
  label: string;
  /** Label for the `inherit` kind (e.g. "Category default"). */
  inheritLabel?: string;
  layout?: 'stacked' | 'inline';
}

/** Kind picker plus the numbers that kind needs. Used by rate fields and schedule periods. */
export function RateValueEditor({
  value,
  onChange,
  kinds,
  label,
  inheritLabel,
  layout = 'inline',
}: RateValueEditorProps) {
  const { t } = useTranslation();
  const kindLabel = (k: RateKind): string =>
    k === 'inherit' ? (inheritLabel ?? t('rate.kinds.inherit')) : t(`rate.kinds.${k}`);
  return (
    <div
      className={
        layout === 'stacked'
          ? 'space-y-1.5'
          : value.kind === 'inherit'
            ? 'grid grid-cols-1'
            : 'grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-1.5'
      }
    >
      <Select
        aria-label={`${label} — ${t('rate.kind')}`}
        value={value.kind}
        options={kinds.map((k) => ({ value: k, label: kindLabel(k) }))}
        onValueChange={(kind) => {
          if (kind !== value.kind) onChange(convert(value, kind));
        }}
      />
      {value.kind === 'fixed' && (
        <PercentField
          aria-label={`${label} — ${t('rate.rate')}`}
          value={value.rate}
          onCommit={(rate) => {
            onChange({ kind: 'fixed', rate });
          }}
        />
      )}
      {value.kind === 'cpiRelative' && (
        <PercentField
          aria-label={`${label} — ${t('rate.offset')}`}
          value={value.offset}
          min={-100}
          max={100}
          onCommit={(offset) => {
            onChange({ kind: 'cpiRelative', offset });
          }}
        />
      )}
      {value.kind === 'distribution' && (
        <div className="grid grid-cols-2 gap-1.5">
          <PercentField
            aria-label={`${label} — ${t('rate.mean')}`}
            value={value.mean}
            onCommit={(mean) => {
              onChange({ ...value, mean });
            }}
          />
          <PercentField
            aria-label={`${label} — ${t('rate.volatility')}`}
            value={value.volatility}
            min={0}
            max={500}
            onCommit={(volatility) => {
              onChange({ ...value, volatility });
            }}
          />
        </div>
      )}
    </div>
  );
}
